import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, RefreshCw, Trash2, Copy, Search, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * LoginDiagnosticsPanel
 *
 * Admin-only view of user-serialization errors caught by
 * `utils/user_validation_log.record_user_validation_error`. Powered by:
 *   GET    /api/admin/validation-errors[?email=...&limit=...]
 *   DELETE /api/admin/validation-errors/{error_id}
 *   DELETE /api/admin/validation-errors
 *
 * The panel replaces the old "grep the backend log" debug workflow.
 * When a customer complains they can't log in, the admin can filter by
 * their email, see the exact fields in Mongo that failed Pydantic
 * validation, and copy a JSON diagnostic bundle for the engineer.
 */
export default function LoginDiagnosticsPanel({ token }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [emailFilter, setEmailFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.set('limit', '200');
      if (emailFilter.trim()) qs.set('email', emailFilter.trim());
      const r = await fetch(`${API_URL}/api/admin/validation-errors?${qs}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [token, emailFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(fetchRows, 30_000);
    return () => clearInterval(t);
  }, [autoRefresh, fetchRows]);

  const dismissRow = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/admin/validation-errors/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRows((prev) => prev.filter((row) => row.id !== id));
      toast.success('Incident dismissed');
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    }
  };

  const clearAll = async () => {
    if (!confirm(`Clear all ${total} recorded incidents? Backend file log keeps a permanent copy.`)) return;
    try {
      const r = await fetch(`${API_URL}/api/admin/validation-errors`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setRows([]);
      setTotal(0);
      toast.success('All incidents cleared');
    } catch (e) {
      toast.error(`Clear-all failed: ${e.message}`);
    }
  };

  const copyDiagnostic = async (row) => {
    // Copy a compact JSON bundle the admin can paste directly to the engineer.
    const bundle = {
      error_id:      row.id,
      where:         row.where,
      when:          row.created_at,
      user_id:       row.user_id,
      user_email:    row.user_email,
      name:          `${row.first_name || ''} ${row.surname || ''}`.trim(),
      request_ip:    row.request_ip,
      failed_fields: row.failed_fields,
    };
    try {
      await navigator.clipboard.writeText(JSON.stringify(bundle, null, 2));
      toast.success('Diagnostic bundle copied — paste to the engineer');
    } catch {
      // Fallback for insecure contexts / old browsers.
      const ta = document.createElement('textarea');
      ta.value = JSON.stringify(bundle, null, 2);
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy');
      ta.remove();
      toast.success('Copied');
    }
  };

  return (
    <div className="max-w-[80rem] mx-auto p-4 space-y-4" data-testid="login-diagnostics-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Login Diagnostics
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Records every 500 Internal Server Error caused by a corrupted user
            document in MongoDB during <code>/api/auth/login</code>,{' '}
            <code>/api/auth/me</code>, or any authenticated endpoint. Copy the
            diagnostic bundle to send to the developer.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              data-testid="login-diagnostics-email-filter"
              placeholder="Filter by email…"
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchRows(); }}
              className="pl-8 w-64"
            />
            {emailFilter && (
              <button
                onClick={() => { setEmailFilter(''); setTimeout(fetchRows, 0); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Clear filter"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            data-testid="login-diagnostics-refresh-btn"
            variant="outline"
            onClick={fetchRows}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <label className="text-xs text-slate-600 flex items-center gap-1 select-none">
            <input
              type="checkbox"
              data-testid="login-diagnostics-autorefresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh 10s
          </label>
          <Button
            data-testid="login-diagnostics-clear-all-btn"
            variant="destructive"
            onClick={clearAll}
            disabled={rows.length === 0}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Clear all
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-600">
        <Badge variant={rows.length ? 'destructive' : 'secondary'} data-testid="login-diagnostics-count">
          {rows.length} shown
        </Badge>
        <span>Total recorded: {total}</span>
      </div>

      {rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 text-sm">
          {loading ? 'Loading…' : 'No login errors recorded. All good ✅'}
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <Card
              key={row.id}
              className="p-4 border-l-4 border-l-amber-500"
              data-testid={`login-diagnostics-row-${row.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Badge variant="outline" className="font-mono">{row.where}</Badge>
                    <span className="text-sm font-semibold truncate">
                      {row.user_email || <em className="text-slate-400">no email</em>}
                    </span>
                    {(row.first_name || row.surname) && (
                      <span className="text-xs text-slate-500">
                        {[row.first_name, row.surname].filter(Boolean).join(' ')}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mb-2 flex flex-wrap gap-x-4 gap-y-1">
                    <span>
                      <strong>error_id:</strong>{' '}
                      <code className="bg-slate-100 px-1 py-0.5 rounded">{row.id}</code>
                    </span>
                    <span>
                      <strong>user_id:</strong>{' '}
                      <code className="bg-slate-100 px-1 py-0.5 rounded truncate max-w-xs inline-block align-bottom">
                        {row.user_id ?? '—'}
                      </code>
                    </span>
                    <span><strong>when:</strong> {row.created_at?.slice(0, 19).replace('T', ' ')}Z</span>
                    {row.request_ip && <span><strong>ip:</strong> {row.request_ip}</span>}
                  </div>
                  <div className="mt-2 space-y-1.5">
                    <div className="text-xs font-semibold uppercase text-slate-500">
                      Failed fields:
                    </div>
                    {(row.failed_fields || []).map((f, i) => (
                      <div
                        key={i}
                        className="text-xs bg-red-50 border border-red-200 rounded px-2 py-1.5"
                      >
                        <span className="font-mono font-semibold text-red-800">{f.field || '(root)'}</span>
                        <span className="mx-1 text-slate-400">·</span>
                        <span className="text-slate-700">{f.msg}</span>
                        <span className="mx-1 text-slate-400">·</span>
                        <span className="text-slate-500">
                          got <code className="bg-white px-1 rounded">{f.value_type}</code>{' '}
                          value <code className="bg-white px-1 rounded">{JSON.stringify(f.value)}</code>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-2 shrink-0">
                  <Button
                    data-testid={`login-diagnostics-copy-btn-${row.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => copyDiagnostic(row)}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" />
                    Copy for engineer
                  </Button>
                  <Button
                    data-testid={`login-diagnostics-dismiss-btn-${row.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => dismissRow(row.id)}
                    className="text-slate-500 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Dismiss
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
